import React, { useState, useEffect } from 'react';
import { asistenciaAPI } from '../api';
import { useAuth } from '../contexts/AuthContext';
import './RegistrosIncompletos.css';

const RegistrosIncompletos = () => {
  const { user } = useAuth();
  const [registros, setRegistros] = useState([]);
  const [sanciones, setSanciones] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  
  // Estados para modales
  const [modalTipo, setModalTipo] = useState(null); // 'salida', 'entrada', 'completo'
  const [registroEditando, setRegistroEditando] = useState(null);
  const [horaEntrada, setHoraEntrada] = useState('');
  const [horaSalida, setHoraSalida] = useState('');
  const [fechaRegistro, setFechaRegistro] = useState('');
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [exportando, setExportando] = useState(false);

  useEffect(() => {
    if (user && (user.rol === 'JEFE' || user.rol === 'ADMIN')) {
      cargarRegistrosIncompletos();
      cargarSanciones();
      cargarUsuarios();
    }
  }, [user, fechaDesde, fechaHasta]);

  const cargarUsuarios = async () => {
    try {
      const response = await asistenciaAPI.getUsuariosDisponibles();
      setUsuarios(response.data || []);
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
    }
  };

  const cargarRegistrosIncompletos = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (fechaDesde) params.fechaDesde = fechaDesde;
      if (fechaHasta) params.fechaHasta = fechaHasta;
      
      const response = await asistenciaAPI.getRegistrosIncompletos(params);
      setRegistros(response.data || []);
    } catch (err) {
      console.error('Error al cargar registros incompletos:', err);
      setError('Error al cargar los registros incompletos');
    } finally {
      setLoading(false);
    }
  };

  const cargarSanciones = async () => {
    try {
      const params = {};
      if (fechaDesde) params.fechaDesde = fechaDesde;
      if (fechaHasta) params.fechaHasta = fechaHasta;
      
      const response = await asistenciaAPI.getSanciones(params);
      setSanciones(response.data || null);
    } catch (err) {
      console.error('Error al cargar sanciones:', err);
    }
  };

  const formatearFecha = (fecha) => {
    const fechaObj = new Date(fecha);
    return fechaObj.toLocaleDateString('es-PE', {
      timeZone: 'America/Lima',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatearHora = (fecha) => {
    if (!fecha) return '-';
    const fechaObj = new Date(fecha);
    return fechaObj.toLocaleTimeString('es-PE', {
      timeZone: 'America/Lima',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatearFechaParaInput = (fecha) => {
    if (!fecha) return '';
    const fechaObj = new Date(fecha);
    const year = fechaObj.getFullYear();
    const month = String(fechaObj.getMonth() + 1).padStart(2, '0');
    const day = String(fechaObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatearDateTimeParaInput = (fecha) => {
    if (!fecha) return '';
    const fechaObj = new Date(fecha);
    const year = fechaObj.getFullYear();
    const month = String(fechaObj.getMonth() + 1).padStart(2, '0');
    const day = String(fechaObj.getDate()).padStart(2, '0');
    const hours = String(fechaObj.getHours()).padStart(2, '0');
    const minutes = String(fechaObj.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const obtenerMaxFecha = () => {
    const hoy = new Date();
    hoy.setDate(hoy.getDate() - 1); // Ayer como máximo
    const year = hoy.getFullYear();
    const month = String(hoy.getMonth() + 1).padStart(2, '0');
    const day = String(hoy.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const abrirModalAgregarSalida = (registro) => {
    setRegistroEditando(registro);
    setModalTipo('salida');
    
    // Establecer hora de salida por defecto (mismo día a las 18:00)
    const fechaRegistro = new Date(registro.fecha);
    fechaRegistro.setHours(18, 0, 0, 0);
    setHoraSalida(formatearDateTimeParaInput(fechaRegistro));
    setHoraEntrada('');
    setFechaRegistro('');
    setUsuarioSeleccionado(null);
  };

  const abrirModalAgregarEntrada = (registro) => {
    setRegistroEditando(registro);
    setModalTipo('entrada');
    
    // Establecer hora de entrada por defecto (mismo día a las 8:00)
    const fechaRegistro = new Date(registro.fecha);
    fechaRegistro.setHours(8, 0, 0, 0);
    setHoraEntrada(formatearDateTimeParaInput(fechaRegistro));
    setHoraSalida('');
    setFechaRegistro('');
    setUsuarioSeleccionado(null);
  };

  const abrirModalCrearCompleto = () => {
    setRegistroEditando(null);
    setModalTipo('completo');
    
    // Establecer valores por defecto (ayer a las 8:00 entrada y 18:00 salida)
    const ayer = new Date();
    ayer.setDate(ayer.getDate() - 1);
    ayer.setHours(8, 0, 0, 0);
    setHoraEntrada(formatearDateTimeParaInput(ayer));
    
    const ayerSalida = new Date(ayer);
    ayerSalida.setHours(18, 0, 0, 0);
    setHoraSalida(formatearDateTimeParaInput(ayerSalida));
    
    setFechaRegistro(formatearFechaParaInput(ayer));
    setUsuarioSeleccionado(null);
  };

  const cerrarModal = () => {
    setModalTipo(null);
    setRegistroEditando(null);
    setHoraEntrada('');
    setHoraSalida('');
    setFechaRegistro('');
    setUsuarioSeleccionado(null);
    setError(null);
  };

  const handleGuardarSalida = async () => {
    if (!horaSalida) {
      setError('Por favor, ingresa la hora de salida');
      return;
    }

    try {
      setGuardando(true);
      setError(null);

      const horaSalidaDate = new Date(horaSalida);
      
      await asistenciaAPI.agregarSalida(registroEditando.id, {
        horaSalida: horaSalidaDate.toISOString()
      });

      alert('✅ Hora de salida agregada correctamente');
      cerrarModal();
      cargarRegistrosIncompletos();
      cargarSanciones();
    } catch (err) {
      console.error('Error al guardar hora de salida:', err);
      setError(err.response?.data?.error || 'Error al guardar la hora de salida');
    } finally {
      setGuardando(false);
    }
  };

  const handleGuardarEntrada = async () => {
    if (!horaEntrada) {
      setError('Por favor, ingresa la hora de entrada');
      return;
    }

    try {
      setGuardando(true);
      setError(null);

      const horaEntradaDate = new Date(horaEntrada);
      
      await asistenciaAPI.agregarEntrada(registroEditando.id, {
        horaEntrada: horaEntradaDate.toISOString()
      });

      alert('✅ Hora de entrada agregada correctamente');
      cerrarModal();
      cargarRegistrosIncompletos();
      cargarSanciones();
    } catch (err) {
      console.error('Error al guardar hora de entrada:', err);
      setError(err.response?.data?.error || 'Error al guardar la hora de entrada');
    } finally {
      setGuardando(false);
    }
  };

  const handleCrearRegistroCompleto = async () => {
    if (!usuarioSeleccionado || !fechaRegistro || !horaEntrada || !horaSalida) {
      setError('Por favor, completa todos los campos');
      return;
    }

    try {
      setGuardando(true);
      setError(null);

      const fechaRegistroDate = new Date(fechaRegistro);
      const horaEntradaDate = new Date(horaEntrada);
      const horaSalidaDate = new Date(horaSalida);

      // Asegurar que las horas sean del mismo día que la fecha
      horaEntradaDate.setFullYear(fechaRegistroDate.getFullYear());
      horaEntradaDate.setMonth(fechaRegistroDate.getMonth());
      horaEntradaDate.setDate(fechaRegistroDate.getDate());
      
      horaSalidaDate.setFullYear(fechaRegistroDate.getFullYear());
      horaSalidaDate.setMonth(fechaRegistroDate.getMonth());
      horaSalidaDate.setDate(fechaRegistroDate.getDate());

      await asistenciaAPI.crearRegistroCompleto({
        usuarioId: usuarioSeleccionado.id,
        fecha: fechaRegistroDate.toISOString().split('T')[0],
        horaEntrada: horaEntradaDate.toISOString(),
        horaSalida: horaSalidaDate.toISOString()
      });

      alert('✅ Registro completo creado correctamente');
      cerrarModal();
      cargarRegistrosIncompletos();
      cargarSanciones();
    } catch (err) {
      console.error('Error al crear registro completo:', err);
      setError(err.response?.data?.error || 'Error al crear el registro completo');
    } finally {
      setGuardando(false);
    }
  };

  const handleExportarExcel = async () => {
    try {
      setExportando(true);
      setError(null);

      const params = {};
      if (fechaDesde) params.fechaDesde = fechaDesde;
      if (fechaHasta) params.fechaHasta = fechaHasta;

      const response = await asistenciaAPI.exportarAsistenciaExcel(params);
      
      // Crear blob y descargar
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const fechaHoy = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `reporte_asistencia_${fechaHoy}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      alert('✅ Reporte Excel descargado correctamente');
    } catch (err) {
      console.error('Error al exportar Excel:', err);
      setError('Error al exportar el reporte Excel');
    } finally {
      setExportando(false);
    }
  };

  const obtenerTipoRegistro = (registro) => {
    if (!registro.horaEntrada && !registro.horaSalida) {
      return 'sin-ambos';
    } else if (!registro.horaEntrada) {
      return 'sin-entrada';
    } else if (!registro.horaSalida) {
      return 'sin-salida';
    }
    return 'completo';
  };

  // Usar usuarios cargados del endpoint
  const usuariosDisponibles = usuarios;

  if (!user || (user.rol !== 'JEFE' && user.rol !== 'ADMIN')) {
    return (
      <div className="registros-incompletos-container">
        <div className="alert-error">
          <span>⚠️</span>
          <span>No tienes permisos para acceder a esta sección</span>
        </div>
      </div>
    );
  }

  return (
    <div className="registros-incompletos-container">
      <div className="registros-header">
        <h2>📋 Registros de Asistencia Incompletos</h2>
        <p>Gestiona los registros de asistencia incompletos de días anteriores</p>
      </div>

      <div className="filtros-section">
        <div className="filtro-item">
          <label>Fecha desde:</label>
          <input
            type="date"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
            max={obtenerMaxFecha()}
          />
        </div>
        <div className="filtro-item">
          <label>Fecha hasta:</label>
          <input
            type="date"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
            max={obtenerMaxFecha()}
          />
        </div>
        <button onClick={cargarRegistrosIncompletos} className="btn-buscar">
          🔍 Buscar
        </button>
        <button onClick={handleExportarExcel} className="btn-exportar" disabled={exportando}>
          {exportando ? '⏳ Exportando...' : '📊 Exportar Excel'}
        </button>
        <button onClick={abrirModalCrearCompleto} className="btn-crear-registro">
          ➕ Crear Registro Completo
        </button>
      </div>

      {/* Resumen de Sanciones */}
      {sanciones && sanciones.sancionesPorUsuario && sanciones.sancionesPorUsuario.length > 0 && (
        <div className="sanciones-section">
          <h3>💰 Resumen de Sanciones</h3>
          <div className="sanciones-table">
            <table>
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Cantidad</th>
                  <th>Total (S/.)</th>
                </tr>
              </thead>
              <tbody>
                {sanciones.sancionesPorUsuario.map((item, index) => (
                  <tr key={index}>
                    <td>{item.nombre}</td>
                    <td>{item.cantidad}</td>
                    <td>S/ {item.total.toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td><strong>TOTAL</strong></td>
                  <td><strong>{sanciones.total.cantidad}</strong></td>
                  <td><strong>S/ {sanciones.total.monto.toFixed(2)}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {error && (
        <div className="alert-error">
          <span>⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {loading ? (
        <div className="loading">Cargando registros...</div>
      ) : registros.length === 0 ? (
        <div className="sin-registros">
          <p>✅ No hay registros incompletos</p>
        </div>
      ) : (
        <div className="registros-list">
          {registros.map((registro) => {
            const tipo = obtenerTipoRegistro(registro);
            const marcadoPorAdmin = registro.marcadoPorAdmin || 
                                   registro.entradaMarcadaPorAdmin || 
                                   registro.salidaMarcadaPorAdmin;
            
            return (
              <div key={registro.id} className="registro-item">
                <div className="registro-info">
                  <div className="registro-usuario">
                    <strong>{registro.usuario.nombre}</strong>
                    {registro.usuario.profesion && (
                      <span className="profesion"> • {registro.usuario.profesion.nombre}</span>
                    )}
                    {marcadoPorAdmin && (
                      <span className="badge-admin">👤 Marcado por administrador</span>
                    )}
                  </div>
                  <div className="registro-fecha">
                    📅 {formatearFecha(registro.fecha)}
                  </div>
                  <div className="registro-horas">
                    {tipo === 'sin-ambos' && (
                      <>
                        <span className="entrada incompleto">❌ Entrada: No registrada</span>
                        <span className="salida incompleto">❌ Salida: No registrada</span>
                      </>
                    )}
                    {tipo === 'sin-entrada' && (
                      <>
                        <span className="entrada incompleto">❌ Entrada: No registrada</span>
                        <span className="salida">✅ Salida: {formatearHora(registro.horaSalida)}</span>
                      </>
                    )}
                    {tipo === 'sin-salida' && (
                      <>
                        <span className="entrada">✅ Entrada: {formatearHora(registro.horaEntrada)}</span>
                        <span className="salida incompleto">❌ Salida: No registrada</span>
                      </>
                    )}
                    {registro.entradaMarcadaPorAdmin && (
                      <span className="badge-admin-small">👤 Entrada por admin</span>
                    )}
                    {registro.salidaMarcadaPorAdmin && (
                      <span className="badge-admin-small">👤 Salida por admin</span>
                    )}
                  </div>
                  {registro.sancion > 0 && (
                    <div className="registro-sancion">
                      ⚠️ Sanción: S/ {registro.sancion.toFixed(2)}
                    </div>
                  )}
                </div>
                <div className="registro-acciones">
                  {tipo === 'sin-salida' && (
                    <button
                      onClick={() => abrirModalAgregarSalida(registro)}
                      className="btn-agregar-salida"
                    >
                      ➕ Agregar Salida
                    </button>
                  )}
                  {tipo === 'sin-entrada' && (
                    <button
                      onClick={() => abrirModalAgregarEntrada(registro)}
                      className="btn-agregar-entrada"
                    >
                      ➕ Agregar Entrada
                    </button>
                  )}
                  {tipo === 'sin-ambos' && (
                    <>
                      <button
                        onClick={() => abrirModalAgregarEntrada(registro)}
                        className="btn-agregar-entrada"
                      >
                        ➕ Agregar Entrada
                      </button>
                      <button
                        onClick={() => abrirModalAgregarSalida(registro)}
                        className="btn-agregar-salida"
                      >
                        ➕ Agregar Salida
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal para agregar salida */}
      {modalTipo === 'salida' && registroEditando && (
        <div className="modal-overlay" onClick={cerrarModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Agregar Hora de Salida</h3>
              <button onClick={cerrarModal} className="btn-cerrar">✕</button>
            </div>
            <div className="modal-body">
              <div className="modal-info">
                <p><strong>Usuario:</strong> {registroEditando.usuario.nombre}</p>
                <p><strong>Fecha:</strong> {formatearFecha(registroEditando.fecha)}</p>
                {registroEditando.horaEntrada && (
                  <p><strong>Hora de Entrada:</strong> {formatearHora(registroEditando.horaEntrada)}</p>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="horaSalida">Hora de Salida:</label>
                <input
                  id="horaSalida"
                  type="datetime-local"
                  value={horaSalida}
                  onChange={(e) => setHoraSalida(e.target.value)}
                  max={formatearDateTimeParaInput(new Date())}
                  required
                />
                <small>Selecciona la fecha y hora de salida (solo días anteriores)</small>
              </div>
              {error && (
                <div className="alert-error-small">
                  {error}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button onClick={cerrarModal} className="btn-cancelar">
                Cancelar
              </button>
              <button
                onClick={handleGuardarSalida}
                disabled={guardando || !horaSalida}
                className="btn-guardar"
              >
                {guardando ? 'Guardando...' : '✅ Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para agregar entrada */}
      {modalTipo === 'entrada' && registroEditando && (
        <div className="modal-overlay" onClick={cerrarModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Agregar Hora de Entrada</h3>
              <button onClick={cerrarModal} className="btn-cerrar">✕</button>
            </div>
            <div className="modal-body">
              <div className="modal-info">
                <p><strong>Usuario:</strong> {registroEditando.usuario.nombre}</p>
                <p><strong>Fecha:</strong> {formatearFecha(registroEditando.fecha)}</p>
                {registroEditando.horaSalida && (
                  <p><strong>Hora de Salida:</strong> {formatearHora(registroEditando.horaSalida)}</p>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="horaEntrada">Hora de Entrada:</label>
                <input
                  id="horaEntrada"
                  type="datetime-local"
                  value={horaEntrada}
                  onChange={(e) => setHoraEntrada(e.target.value)}
                  max={formatearDateTimeParaInput(new Date())}
                  required
                />
                <small>Selecciona la fecha y hora de entrada (solo días anteriores)</small>
              </div>
              {error && (
                <div className="alert-error-small">
                  {error}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button onClick={cerrarModal} className="btn-cancelar">
                Cancelar
              </button>
              <button
                onClick={handleGuardarEntrada}
                disabled={guardando || !horaEntrada}
                className="btn-guardar"
              >
                {guardando ? 'Guardando...' : '✅ Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para crear registro completo */}
      {modalTipo === 'completo' && (
        <div className="modal-overlay" onClick={cerrarModal}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Crear Registro Completo</h3>
              <button onClick={cerrarModal} className="btn-cerrar">✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="usuarioCompleto">Usuario:</label>
                <select
                  id="usuarioCompleto"
                  value={usuarioSeleccionado?.id || ''}
                  onChange={(e) => {
                    const usuario = usuariosDisponibles.find(u => u.id === parseInt(e.target.value));
                    setUsuarioSeleccionado(usuario || null);
                  }}
                  required
                >
                  <option value="">Selecciona un usuario</option>
                  {usuariosDisponibles.map(usuario => (
                    <option key={usuario.id} value={usuario.id}>
                      {usuario.nombre} {usuario.profesion ? `(${usuario.profesion.nombre})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="fechaRegistro">Fecha del Registro:</label>
                <input
                  id="fechaRegistro"
                  type="date"
                  value={fechaRegistro}
                  onChange={(e) => setFechaRegistro(e.target.value)}
                  max={obtenerMaxFecha()}
                  required
                />
                <small>Solo se pueden crear registros para días anteriores</small>
              </div>
              <div className="form-group">
                <label htmlFor="horaEntradaCompleto">Hora de Entrada:</label>
                <input
                  id="horaEntradaCompleto"
                  type="datetime-local"
                  value={horaEntrada}
                  onChange={(e) => setHoraEntrada(e.target.value)}
                  max={formatearDateTimeParaInput(new Date())}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="horaSalidaCompleto">Hora de Salida:</label>
                <input
                  id="horaSalidaCompleto"
                  type="datetime-local"
                  value={horaSalida}
                  onChange={(e) => setHoraSalida(e.target.value)}
                  max={formatearDateTimeParaInput(new Date())}
                  required
                />
              </div>
              {error && (
                <div className="alert-error-small">
                  {error}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button onClick={cerrarModal} className="btn-cancelar">
                Cancelar
              </button>
              <button
                onClick={handleCrearRegistroCompleto}
                disabled={guardando || !usuarioSeleccionado || !fechaRegistro || !horaEntrada || !horaSalida}
                className="btn-guardar"
              >
                {guardando ? 'Guardando...' : '✅ Crear Registro'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrosIncompletos;
